import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const StrategyPlanner = ({ data, loading = false }) => {
  const [selectedCategory, setSelectedCategory] = useState(0);

  if (loading || !data) {
    return (
      <div className="space-y-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-muted rounded w-1/4 mb-4" />
            <div className="space-y-3">
              <div className="h-4 bg-muted rounded" />
              <div className="h-4 bg-muted rounded w-3/4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const strategicGuidance = data?.strategicGuidance || [];
  const preparationPlan = data?.preparationPlan;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-destructive';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return 'AlertTriangle';
      case 'medium': return 'Clock';
      case 'low': return 'CheckCircle';
      default: return 'Circle';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'performance': return 'TrendingUp';
      case 'skills': return 'BookOpen';
      case 'networking': return 'Users';
      case 'visibility': return 'Eye';
      case 'leadership': return 'Crown';
      case 'communication': return 'MessageSquare';
      default: return 'Target';
    }
  };

  return (
    <div className="space-y-6">
      {/* Strategic Guidance Overview */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="Map" size={16} className="text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Strategic Action Plan</h3>
        </div>

        {strategicGuidance?.length > 0 && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 mb-4">
              {strategicGuidance?.map((guidance, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedCategory(index)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === index
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  <Icon name={getCategoryIcon(guidance?.category)} size={14} />
                  <span>{guidance?.category}</span>
                </button>
              ))}
            </div>

            <div className="bg-muted/50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    strategicGuidance?.[selectedCategory]?.priority === 'high' ? 'bg-destructive/10' :
                    strategicGuidance?.[selectedCategory]?.priority === 'medium'? 'bg-warning/10' : 'bg-success/10'
                  }`}>
                    <Icon 
                      name={getCategoryIcon(strategicGuidance?.[selectedCategory]?.category)} 
                      size={20} 
                      className="text-primary" 
                    />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-foreground">
                      {strategicGuidance?.[selectedCategory]?.title}
                    </h4>
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={getPriorityIcon(strategicGuidance?.[selectedCategory]?.priority)} 
                        size={14} 
                        className={getPriorityColor(strategicGuidance?.[selectedCategory]?.priority)} 
                      />
                      <span className={`text-sm font-medium ${getPriorityColor(strategicGuidance?.[selectedCategory]?.priority)}`}>
                        {strategicGuidance?.[selectedCategory]?.priority?.toUpperCase()} PRIORITY
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-muted-foreground mb-4 leading-relaxed">
                {strategicGuidance?.[selectedCategory]?.description}
              </p>

              <div className="space-y-3">
                <h5 className="font-medium text-foreground flex items-center space-x-2">
                  <Icon name="List" size={16} className="text-primary" />
                  <span>Action Items</span>
                </h5>
                <div className="space-y-2">
                  {strategicGuidance?.[selectedCategory]?.actionItems?.map((item, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-card rounded-lg">
                      <div className="w-5 h-5 bg-primary/10 rounded-full flex items-center justify-center mt-0.5">
                        <div className="w-2 h-2 bg-primary rounded-full" />
                      </div>
                      <p className="text-sm text-muted-foreground flex-1">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Preparation Timeline */}
      {preparationPlan && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-8 h-8 bg-warning/10 rounded-full flex items-center justify-center">
              <Icon name="Calendar" size={16} className="text-warning" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Execution Timeline</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Immediate Actions */}
            <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-6 h-6 bg-destructive/10 rounded-full flex items-center justify-center">
                  <Icon name="Zap" size={14} className="text-destructive" />
                </div>
                <h4 className="font-semibold text-foreground">Immediate</h4>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Next 1-2 weeks</p>
              <div className="space-y-2">
                {preparationPlan?.immediateActions?.map((action, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <div className="w-1 h-1 bg-destructive rounded-full mt-2 flex-shrink-0" />
                    <p className="text-xs text-muted-foreground">{action}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Short Term Goals */}
            <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-6 h-6 bg-warning/10 rounded-full flex items-center justify-center">
                  <Icon name="Target" size={14} className="text-warning" />
                </div>
                <h4 className="font-semibold text-foreground">Short Term</h4>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Next 1-3 months</p>
              <div className="space-y-2">
                {preparationPlan?.shortTermGoals?.map((goal, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <div className="w-1 h-1 bg-warning rounded-full mt-2 flex-shrink-0" />
                    <p className="text-xs text-muted-foreground">{goal}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Long Term Strategy */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="TrendingUp" size={14} className="text-primary" />
                </div>
                <h4 className="font-semibold text-foreground">Long Term</h4>
              </div>
              <p className="text-xs text-muted-foreground mb-3">3+ months</p>
              <div className="space-y-2">
                {preparationPlan?.longTermStrategy?.map((strategy, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <div className="w-1 h-1 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <p className="text-xs text-muted-foreground">{strategy}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Negotiation Prep */}
            <div className="bg-success/5 border border-success/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-6 h-6 bg-success/10 rounded-full flex items-center justify-center">
                  <Icon name="MessageSquare" size={14} className="text-success" />
                </div>
                <h4 className="font-semibold text-foreground">Negotiation</h4>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Key tips</p>
              <div className="space-y-2">
                {preparationPlan?.negotiationTips?.slice(0, 3)?.map((tip, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <div className="w-1 h-1 bg-success rounded-full mt-2 flex-shrink-0" />
                    <p className="text-xs text-muted-foreground">{tip}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress Tracking */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-secondary/10 rounded-full flex items-center justify-center">
            <Icon name="CheckSquare" size={16} className="text-secondary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Progress Tracking</h3>
        </div>

        <div className="bg-muted/50 rounded-lg p-4">
          <h4 className="font-medium text-foreground mb-3">Recommended Tracking Methods</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Icon name="Calendar" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Weekly Reviews</span>
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                Schedule weekly check-ins to review progress on immediate actions and adjust strategies as needed.
              </p>
            </div>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Icon name="BarChart3" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Milestone Tracking</span>
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                Set clear milestones for short and long-term goals with measurable outcomes.
              </p>
            </div>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Feedback Collection</span>
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                Regularly gather feedback from colleagues and supervisors to gauge progress.
              </p>
            </div>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Icon name="FileText" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Documentation</span>
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                Keep detailed records of achievements, projects, and impact metrics.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategyPlanner;